// JWT auth, mirroring the Mac client's AuthSession pattern.
//
//   - `access` lives in memory only (15-min lifetime; cheap to refresh).
//   - `refresh` lives in localStorage (30-day lifetime). XSS exposure is
//     accepted because the SPA runs same-origin with the API; the
//     premium hardening would move refresh into an HttpOnly cookie
//     issued by the server. Out of scope for v1.
//   - On 401 with an `access`, swap in `refresh`, retry once, then bail.
//
// Storage keys are scoped `lucy.web.*` so the user-facing client and the
// operator console (`lucy.admin.*`) can coexist on the same origin
// without stepping on each other's tokens.

const REFRESH_KEY = "lucy.web.refresh"
const ME_KEY = "lucy.web.me"

export type Tier = "admin" | "pro" | "team" | "free"

export interface MeOut {
  id: string
  email: string | null
  display_name: string | null
  tier: Tier
  created_at: string
  email_verified?: boolean
  email_verified_at?: string | null
  totp_enabled?: boolean
  paused?: boolean
  subscription_status?: string | null
  tz?: string | null
  ui_locale?: "el" | "en" | null
  reply_language?: "mirror" | "el" | "en" | null
  onboarded_at?: string | null
  terms_accepted_at?: string | null
  notify_prefs?: Record<string, boolean>
}

interface TokenPair {
  access_token: string
  refresh_token: string
  expires_in?: number
}

let access: string | null = null
// The refresh token lives in an HttpOnly cookie set by the server; the
// page only keeps the short-lived access token in memory. A value left in
// localStorage by an older build is used once to migrate, then dropped.
const SIGNED_IN_KEY = "lucy.web.signed_in"
let refresh: string | null = localStorage.getItem(REFRESH_KEY)
let cachedMe: MeOut | null = readMeCache()

function readMeCache(): MeOut | null {
  try {
    const raw = localStorage.getItem(ME_KEY)
    return raw ? (JSON.parse(raw) as MeOut) : null
  } catch {
    return null
  }
}

function writeMeCache(me: MeOut | null) {
  if (me) localStorage.setItem(ME_KEY, JSON.stringify(me))
  else localStorage.removeItem(ME_KEY)
}

export const auth = {
  isLoggedIn(): boolean {
    if (access || refresh) return true
    try { return localStorage.getItem(SIGNED_IN_KEY) === "1" } catch { return false }
  },

  /** The stored refresh token, for endpoints that need to identify this device. */
  _refreshToken(): string | null {
    return refresh
  },

  me(): MeOut | null {
    return cachedMe
  },

  isAdmin(): boolean {
    return cachedMe?.tier === "admin"
  },

  async signup(
    email: string,
    password: string,
    displayName?: string,
    inviteCode?: string,
    acceptTerms?: boolean,
  ): Promise<MeOut> {
    const r = await fetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        display_name: displayName || null,
        invite_code: inviteCode?.trim() || null,
        accept_terms: acceptTerms ?? null,
      }),
    })
    if (!r.ok) {
      const body = await safeJSON(r)
      throw new Error(body?.detail || `Signup failed (${r.status})`)
    }
    const tokens = (await r.json()) as TokenPair
    setTokens(tokens)
    await this.refreshMe()
    if (!cachedMe) throw new Error("Signup succeeded but /api/auth/me returned nothing")
    return cachedMe
  },

  async login(email: string, password: string, totp?: string): Promise<MeOut> {
    const r = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, totp: totp || null }),
    })
    if (r.status === 412) {
      // Server signals: TOTP required, retry with code.
      throw Object.assign(new Error("totp_required"), { code: "totp_required" })
    }
    if (!r.ok) {
      const body = await safeJSON(r)
      throw new Error(body?.detail || `Login failed (${r.status})`)
    }
    const tokens = (await r.json()) as TokenPair
    setTokens(tokens)
    await this.refreshMe()
    if (!cachedMe) throw new Error("Login succeeded but /api/auth/me returned nothing")
    return cachedMe
  },

  /** Finish a sign-in whose tokens came from another ceremony (passkey). */
  async loginWithTokens(tokens: TokenPair): Promise<MeOut> {
    setTokens(tokens)
    await this.refreshMe()
    if (!cachedMe) throw new Error("Signed in but /api/auth/me returned nothing")
    return cachedMe
  },

  async logout(): Promise<void> {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(refresh ? { refresh_token: refresh } : {}),
      })
    } catch {
      // Network blip on logout shouldn't block local cleanup.
    }
    clear()
  },

  async forgot(email: string): Promise<void> {
    await fetch("/api/auth/forgot", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    })
    // Server returns 200 either way — we never reveal whether the email exists.
  },

  async reset(token: string, password: string): Promise<void> {
    const r = await fetch("/api/auth/reset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, new_password: password }),
    })
    if (!r.ok && r.status !== 204) {
      const body = await safeJSON(r)
      throw new Error(body?.detail || `Reset failed (${r.status})`)
    }
  },

  async resendVerify(): Promise<void> {
    if (!access && !(await tryRefresh())) throw new Error("Not signed in")
    const r = await fetch("/api/auth/resend-verify", {
      method: "POST",
      headers: { Authorization: `Bearer ${access}` },
    })
    if (!r.ok && r.status !== 204) {
      const body = await safeJSON(r)
      throw new Error(body?.detail || `Resend failed (${r.status})`)
    }
  },

  /** Refetch /api/auth/me into the cached identity. */
  async refreshMe(): Promise<MeOut | null> {
    if (!access && !(await tryRefresh())) {
      cachedMe = null
      writeMeCache(null)
      return null
    }
    try {
      const r = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${access}` },
      })
      if (!r.ok) throw new Error(`/api/auth/me ${r.status}`)
      cachedMe = (await r.json()) as MeOut
      writeMeCache(cachedMe)
      return cachedMe
    } catch {
      cachedMe = null
      writeMeCache(null)
      return null
    }
  },

  /** Internal — used by api(). Exported for tests. */
  async _accessToken(): Promise<string | null> {
    if (access) return access
    if (await tryRefresh()) return access
    return null
  },

  /** Internal — used by api() to roll on 401. */
  async _retryRefresh(): Promise<boolean> {
    return tryRefresh()
  },
}

function setTokens(t: TokenPair) {
  access = t.access_token
  refresh = null
  try { localStorage.removeItem(REFRESH_KEY); localStorage.setItem(SIGNED_IN_KEY, "1") } catch { /* storage blocked */ }
}

function clear() {
  access = null
  refresh = null
  cachedMe = null
  try { localStorage.removeItem(REFRESH_KEY); localStorage.removeItem(SIGNED_IN_KEY) } catch { /* storage blocked */ }
  writeMeCache(null)
}

// Single-flight: with rotating refresh tokens, two concurrent refreshes
// would reuse the old token and trip the server's reuse detection.
let refreshing: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing
  refreshing = _tryRefresh().finally(() => { refreshing = null })
  return refreshing
}

async function _tryRefresh(): Promise<boolean> {
  let signedIn = false
  try { signedIn = localStorage.getItem(SIGNED_IN_KEY) === "1" } catch { /* storage blocked */ }
  if (!refresh && !signedIn) return false
  try {
    const r = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(refresh ? { refresh_token: refresh } : {}),
    })
    if (!r.ok) {
      // Refresh that doesn't verify = forced sign-out. RFC 6819 reuse
      // detection on the server side will revoke the family for us.
      clear()
      return false
    }
    const tokens = (await r.json()) as TokenPair
    setTokens(tokens)
    return true
  } catch {
    return false
  }
}

async function safeJSON(r: Response): Promise<{ detail?: string } | null> {
  try {
    return (await r.json()) as { detail?: string }
  } catch {
    return null
  }
}
